/**
 * Supply Service
 * 
 * Fetches supplies from Firestore Supplies collection
 */

import { collection, getDocs, query, where, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface Supply {
  id?: string;
  code: string; // Mapped from ItemCode in database for consistency
  ItemCode?: string; // Database field name
  description: string;
  descriptionEN?: string;
  descriptionFR?: string;
  category: string;
  categoryEN?: string;
  categoryFR?: string;
  retail?: string;
  note?: string;
  status?: 'available' | 'out-of-stock';
}

export interface SupplyFilters {
  category?: string;
  searchTerm?: string;
}

/**
 * Item codes where Firestore `retail` is the unit price for qty 1 (Order Supplies).
 */
export const SUPPLY_UNIT_PRICED_ITEM_CODES = new Set([
  '143261',
  '133514',
  '138287',
  '143311',
  '143312',
  '143313',
  '143317',
]);

/** Parse `retail` string to a non-negative number, or 0 if missing / non-numeric / markers like NA. */
export function parseSupplyRetailToUnitPrice(retail?: string | null): number {
  if (retail == null || retail === '') return 0;
  const s = String(retail).trim();
  if (/^(NA|N\/A|PPSO)$/i.test(s)) return 0;
  const n = parseFloat(s.replace(/[$,\s]/g, ''));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function itemCodeSegments(code: string): string[] {
  return code
    .split(/[,]+/)
    .map((c) => c.trim())
    .filter(Boolean)
    .flatMap((c) => c.split(/\s+/).filter(Boolean));
}

/**
 * Unit price (qty 1) for order-supplies line items — only for {@link SUPPLY_UNIT_PRICED_ITEM_CODES}.
 */
export function getSupplyUnitPriceForOrder(supply: Supply): number {
  const raw = supply.ItemCode || supply.code || '';
  const segments = itemCodeSegments(raw);
  const hasPricedCode = segments.some((c) => SUPPLY_UNIT_PRICED_ITEM_CODES.has(c));
  if (!hasPricedCode) return 0;
  return parseSupplyRetailToUnitPrice(supply.retail);
}

/**
 * Fetch all supplies from Firestore
 */
export async function getAllSupplies(): Promise<Supply[]> {
  try {
    const suppliesRef = collection(db, 'Supplies');
    // Use ItemCode for ordering (will work after migration, or fallback handled in catch)
    let snapshot;
    try {
      snapshot = await getDocs(query(suppliesRef, orderBy('ItemCode')));
    } catch (error: any) {
      // Fallback to 'code' if ItemCode index doesn't exist yet (backward compatibility)
      if (error?.code === 'failed-precondition') {
        snapshot = await getDocs(query(suppliesRef, orderBy('code')));
      } else {
        throw error;
      }
    }
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      // Map ItemCode to code for consistency in application code
      return {
        id: doc.id,
        code: data.ItemCode || data.code || '', // Use ItemCode if available, fallback to code
        ...data
      } as Supply;
    });
  } catch (error) {
    console.error('Error fetching supplies:', error);
    throw error;
  }
}

/**
 * Fetch supplies with filters
 */
export async function getSupplies(filters?: SupplyFilters): Promise<Supply[]> {
  try {
    let suppliesQuery: any = collection(db, 'Supplies');
    const constraints: any[] = [];

    // Apply filters
    if (filters?.category) {
      constraints.push(where('category', '==', filters.category));
    }

    // Add ordering - use ItemCode (will work after migration)
    // Note: If ItemCode index doesn't exist, this will fail and need index creation
    constraints.push(orderBy('ItemCode'));

    if (constraints.length > 0) {
      suppliesQuery = query(suppliesQuery, ...constraints);
    }

    const snapshot = await getDocs(suppliesQuery);
    let supplies = snapshot.docs.map(doc => {
      const data = doc.data();
      // Map ItemCode to code for consistency in application code
      return {
        id: doc.id,
        code: data.ItemCode || data.code || '', // Use ItemCode if available, fallback to code
        ...data
      } as Supply;
    });

    // Apply search filter in memory (Firestore doesn't support full-text search)
    if (filters?.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      supplies = supplies.filter(supply => {
        const itemCode = supply.ItemCode || supply.code || '';
        return (
          (supply.description || supply.descriptionEN || '').toLowerCase().includes(searchLower) ||
          (supply.descriptionFR || '').toLowerCase().includes(searchLower) ||
          itemCode.toLowerCase().includes(searchLower) ||
          (supply.category || supply.categoryEN || '').toLowerCase().includes(searchLower)
        );
      });
    }

    return supplies;
  } catch (error) {
    console.error('Error fetching supplies with filters:', error);
    throw error;
  }
}

/**
 * Fetch supplies by category
 */
export async function getSuppliesByCategory(category: string): Promise<Supply[]> {
  return getSupplies({ category });
}

/**
 * Get multiple supplies by ItemCodes (batch fetch)
 * Firestore 'in' queries are limited to 10 items, so this handles batching
 */
export async function getSuppliesByCodes(itemCodes: string[]): Promise<Map<string, Supply>> {
  const supplyMap = new Map<string, Supply>();
  
  if (!itemCodes || itemCodes.length === 0) {
    return supplyMap;
  }

  try {
    // Remove duplicates and filter out empty values
    const uniqueCodes = Array.from(new Set(itemCodes.filter(code => code && code.trim())));
    
    if (uniqueCodes.length === 0) {
      return supplyMap;
    }

    const suppliesRef = collection(db, 'Supplies');
    const BATCH_SIZE = 10; // Firestore 'in' query limit
    
    // Process in batches of 10
    for (let i = 0; i < uniqueCodes.length; i += BATCH_SIZE) {
      const batch = uniqueCodes.slice(i, i + BATCH_SIZE);
      
      try {
        // Try ItemCode first, fallback to code if needed
        let snapshot;
        try {
          const q = query(suppliesRef, where('ItemCode', 'in', batch));
          snapshot = await getDocs(q);
        } catch (error: any) {
          // Fallback to 'code' field if ItemCode query fails
          if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
            const q = query(suppliesRef, where('code', 'in', batch));
            snapshot = await getDocs(q);
          } else {
            throw error;
          }
        }
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          const supply: Supply = {
            id: doc.id,
            code: data.ItemCode || data.code || '',
            ...data
          };
          // Use ItemCode or code as the key
          const key = supply.ItemCode || supply.code;
          if (key) {
            supplyMap.set(key, supply);
          }
        });
      } catch (error: any) {
        console.error(`Error fetching batch of supplies (codes: ${batch.join(', ')}):`, error);
        // Continue with next batch even if one fails
      }
    }
    
    console.log(`[SupplyService] Fetched ${supplyMap.size} supplies from ${uniqueCodes.length} unique ItemCodes`);
    return supplyMap;
  } catch (error) {
    console.error('Error fetching supplies by codes:', error);
    throw error;
  }
}

export async function updateSupplyStatus(
  supplyId: string,
  status: 'available' | 'out-of-stock'
): Promise<void> {
  try {
    const supplyRef = doc(db, 'Supplies', supplyId);
    await updateDoc(supplyRef, { status });
  } catch (error) {
    console.error(`Error updating supply status for ${supplyId}:`, error);
    throw error;
  }
}

export async function getAllSuppliesWithStatus(): Promise<Supply[]> {
  const supplies = await getAllSupplies();
  return supplies.map(s => ({
    ...s,
    status: s.status || 'available'
  }));
}

